// Pure functions that transform page model coordinates (PDF points) into layout
// coordinates (CSS pixels) for scroll positioning and visibility testing.

import { LinearTransform, Rect, rectIntersects } from '@st/geom'
import { ViewerPage } from './models'
import { resolveRenderPlan } from './document-render-policy'
import type { DocumentLocation } from './page-layers'

export const PAGE_VERTICAL_SPACING_PX = 36

export type PageRect = {
  pageNumber: number
  rect: Rect
}

export type ViewerViewportState = {
  pagesToRender: number[]
  activePageNumber: number | null
}

export function selPageTransform(zoom: number, cssPxPerPdfPt: number) {
  return LinearTransform.scale(zoom * cssPxPerPdfPt)
}

/**
 * Calculate the y-coordinate of the scroll position needed to display a page in
 * the viewport.
 *
 * This makes use of the page dimensions, the linear transform applied (zoom, rotation, etc),
 * and the vertical spacing between pages to calculate where one would scroll to.
 */
export function selScrollPagePositionY(
  pages: ViewerPage[],
  linearTransform: LinearTransform,
  pageNumber: number
) {
  const transformedVerticalSpacing = LinearTransform.apply(
    { x: 0, y: PAGE_VERTICAL_SPACING_PX },
    linearTransform
  ).y

  // To scroll to a page, we sum the transformed heights of *preceding* pages.
  // Any page policy (filtered/ collapsed heights) should already be reflected in `pages`.
  return pages
    .filter((page) => page.pageNumber < pageNumber)
    .reduce((acc, page) => {
      const transformedPageHeight = LinearTransform.apply(
        { x: 0, y: page.bounds.height },
        linearTransform
      ).y

      return acc + transformedVerticalSpacing + transformedPageHeight
    }, 0)
}

export function selScrollDocumentLocationRect(
  pages: ViewerPage[],
  linearTransform: LinearTransform,
  location: DocumentLocation
): Rect {
  const pageOffsetY = selScrollPagePositionY(pages, linearTransform, location.pageNumber)

  if (!location.bounds) {
    const page = pages.find((candidate) => candidate.pageNumber == location.pageNumber)
    if (!page) {
      return {
        x: 0,
        y: pageOffsetY,
        width: 0,
        height: 0
      }
    }

    const transformedBounds = LinearTransform.apply(page.bounds, linearTransform)
    return {
      x: transformedBounds.x,
      y: pageOffsetY,
      width: transformedBounds.width,
      height: transformedBounds.height
    }
  }

  const transformedBounds = LinearTransform.apply(location.bounds, linearTransform)
  return {
    x: transformedBounds.x,
    y: pageOffsetY + transformedBounds.y,
    width: transformedBounds.width,
    height: transformedBounds.height
  }
}

export function resolvePageRects(
  pages: ViewerPage[],
  linearTransform: LinearTransform
): PageRect[] {
  const transformedVerticalSpacing = LinearTransform.apply(
    { x: 0, y: PAGE_VERTICAL_SPACING_PX },
    linearTransform
  ).y

  let currentTopPx = 0

  return pages.map((page) => {
    const transformedBounds = LinearTransform.apply(page.bounds, linearTransform)
    const pageRect: PageRect = {
      pageNumber: page.pageNumber,
      rect: {
        x: 0,
        y: currentTopPx,
        width: transformedBounds.width,
        height: transformedBounds.height
      }
    }

    currentTopPx += transformedBounds.height + transformedVerticalSpacing
    return pageRect
  })
}

/**
 * Synchronous rect-intersection test: determines which pages overlap the
 * viewport. Deterministic — visibility is always consistent with the scroll
 * position that triggered it.
 */
export function resolveVisiblePageNumbers(pageRects: PageRect[], viewportRect: Rect): number[] {
  return pageRects.flatMap((pageRect) => {
    return rectIntersects(pageRect.rect, viewportRect) ? [pageRect.pageNumber] : []
  })
}

export function resolveViewerViewportState({
  pageRects,
  viewportRect
}: {
  pageRects: PageRect[]
  viewportRect: Rect
}): ViewerViewportState {
  const visiblePageNumbers = resolveVisiblePageNumbers(pageRects, viewportRect)
  const pagesToRender = resolveRenderPlan(
    visiblePageNumbers,
    pageRects.map((pageRect) => pageRect.pageNumber),
    viewportRect.width
  )

  return {
    pagesToRender,
    activePageNumber: visiblePageNumbers[0] ?? null
  }
}
